import type { EntityCacheConfig } from '../types';
import { PERSIST_MAX_AGE } from './constant';

/**
 * Pre-defined entity configurations for common entities
 *
 * Use these for consistent query keys and cache invalidation across web dashboards and apps.
 */
export const ENTITY_CONFIGS = {
  order: {
    singular: 'order',
    plural: 'orders',
    queryKeyPatterns: [
      'order',
      'orders',
      'loads',
      'active-orders',
      'published-orders',
      'jobs',
      'analytics-overview',
      'driver',
      'drivers',
      'vehicle',
      'vehicles',
      'offer',
      'offers',
      'eligible-legs',
    ],
    usesInfiniteQueries: true,
  },
  message: {
    singular: 'message',
    plural: 'messages',
    queryKeyPatterns: ['message', 'messages', 'order', 'orders'],
    usesInfiniteQueries: false,
  },
  offer: {
    singular: 'offer',
    plural: 'offers',
    queryKeyPatterns: [
      'offer',
      'offers',
      'offer-inbox',
      'offer-outbox',
      'load-board',
      // Offer accept writes a contract → invalidate Order keys too.
      'order',
      'orders',
    ],
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
    usesInfiniteQueries: true,
  },
  publishedOrder: {
    singular: 'published-order',
    plural: 'published-orders',
    queryKeyPatterns: ['published-orders', 'jobs'],
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
    usesInfiniteQueries: true,
  },
  activeOrders: {
    singular: 'active-order',
    plural: 'active-orders',
    queryKeyPatterns: ['active-orders'],
    staleTime: 30 * 1000, // 30s — operational data, changes frequently
    refetchOnWindowFocus: true,
    usesInfiniteQueries: true,
  },
  waybill: {
    singular: 'waybill',
    plural: 'waybills',
    queryKeyPatterns: ['waybill'],
  },
  invoice: {
    singular: 'invoice',
    plural: 'invoices',
    // Capturing a delivered leg into an invoice also writes an
    // `AccountTransaction` (type: `leg_capture`) and bumps the
    // bilateral `Account` balance. Bust those reads so the
    // Account drawer / list refreshes alongside the invoice list
    // after `useCaptureLegsApi` succeeds. Same goes for the
    // manual VAT-proof upload — patching `period.due` is read by
    // the Account drawer's invoice rows.
    queryKeyPatterns: [
      'invoice',
      'invoices',
      'account',
      'accounts',
      'account-transaction',
      'account-transactions',
      // Capturing a leg into an invoice removes it from the
      // eligible-legs picker. Bust the picker key so a successful
      // capture takes the row off the list immediately.
      'eligible-legs',
    ],
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
    usesInfiniteQueries: true,
  },
  /**
   * Derived "delivered orders waiting to be invoiced" list backing
   * the `EligibleLegsDrawer` picker. Its underlying data is a
   * function of both orders (price, contracts, status) and invoices
   * (which legs are already captured), so we cross-bust it
   * whenever either changes — see the `'eligible-legs'` entry on
   * the `order` and `invoice` entities above. Its own mutation
   * path is empty (the picker doesn't write).
   */
  eligibleLegs: {
    singular: 'eligible-legs',
    plural: 'eligible-legs',
    queryKeyPatterns: [
      'eligible-legs',
      'order',
      'orders',
      'invoice',
      'invoices',
    ],
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
  },
  member: {
    singular: 'member',
    plural: 'members',
    queryKeyPatterns: [
      'member',
      'members',
      'users',
      'currentMember',
      'driver-members',
      'driver',
      'drivers',
      'vehicle',
      'vehicles',
    ],
    usesInfiniteQueries: true,
  },
  driverMember: {
    singular: 'driver-member',
    plural: 'driver-members',
    queryKeyPatterns: [
      'driver-member',
      'driver-members',
      'driver',
      'drivers',
      'vehicle',
      'vehicles',
    ],
    usesInfiniteQueries: true,
  },
  driver: {
    singular: 'driver',
    plural: 'drivers',
    queryKeyPatterns: ['driver', 'drivers', 'vehicle', 'vehicles'],
    usesInfiniteQueries: true,
  },
  vehicle: {
    singular: 'vehicle',
    plural: 'vehicles',
    queryKeyPatterns: ['vehicle', 'vehicles', 'driver', 'drivers'],
    usesInfiniteQueries: true,
  },
  client: {
    singular: 'client',
    plural: 'clients',
    // Client mutations also touch contacts on the backend (a Client is
    // the unified CRM entity that supersedes Customer; new contacts are
    // created off the same flow). So we also invalidate contact-related
    // query keys (table, selector, etc.).
    queryKeyPatterns: [
      'client',
      'clients',
      'client-modal-clients',
      'contact',
      'contacts',
      'contactsClient',
    ],
    usesInfiniteQueries: true,
  },
  contact: {
    singular: 'contact',
    plural: 'contacts',
    queryKeyPatterns: ['contact', 'contacts', 'contactsClient', 'savedLocation'],
    usesInfiniteQueries: true,
  },
  /**
   * Lead CRM (admin-only mini-CRM). Mutating a Lead's status / people /
   * actions / assignment / convert-link all run through the same
   * entity so the list + drawer + analytics tile invalidate together.
   * `lead-analytics` is co-listed so a status / convertLink mutation
   * also busts the funnel rollup.
   */
  lead: {
    singular: 'lead',
    plural: 'leads',
    queryKeyPatterns: ['lead', 'leads', 'lead-analytics'],
    usesInfiniteQueries: true,
  },
  leadAnalytics: {
    singular: 'lead-analytics',
    plural: 'lead-analytics',
    queryKeyPatterns: ['lead-analytics', 'lead', 'leads'],
    staleTime: 60 * 1000, // 1 min — analytics rollups
    refetchOnWindowFocus: true,
  },
  /**
   * Auth-v2 network User surface. Admin-only Users page + reusable
   * `<UserDrawer />` opened by userId. Mutating a user's `status`
   * (block / activate) also busts the members list because
   * `Member` is a projection over `User.orgs[i]`.
   */
  user: {
    singular: 'user',
    plural: 'users',
    queryKeyPatterns: ['user', 'users', 'user-analytics', 'member', 'members'],
    usesInfiniteQueries: true,
  },
  userAnalytics: {
    singular: 'user-analytics',
    plural: 'user-analytics',
    queryKeyPatterns: ['user-analytics', 'user', 'users'],
    staleTime: 60 * 1000,
    refetchOnWindowFocus: true,
  },
  savedLocation: {
    singular: 'saved-location',
    plural: 'saved-locations',
    queryKeyPatterns: [
      'saved-location',
      'saved-locations',
      'savedLocationsList',
      'locationAutocomplete',
    ],
    usesInfiniteQueries: true,
  },
  address: {
    singular: 'address',
    plural: 'addresses',
    queryKeyPatterns: ['address'],
  },
  autocomplete: {
    singular: 'autocomplete',
    plural: 'autocomplete',
    queryKeyPatterns: ['autocomplete'],
  },
  googleSearch: {
    singular: 'google-search',
    plural: 'google-searches',
    queryKeyPatterns: ['google-search'],
  },
  analyticsOverview: {
    singular: 'analytics-overview',
    plural: 'analytics-overviews',
    queryKeyPatterns: ['analytics-overview'],
    staleTime: 60 * 1000, // 1 min — analytics data goes stale quickly
    refetchOnWindowFocus: true, // refresh when user switches back to the tab
  },
  wallet: {
    singular: 'wallet',
    plural: 'wallets',
    queryKeyPatterns: ['wallet', 'balance'],
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
  },
  payment: {
    singular: 'payment',
    plural: 'payments',
    queryKeyPatterns: ['payment'],
  },
  unpaidTotal: {
    singular: 'unpaid-total',
    plural: 'unpaid-totals',
    queryKeyPatterns: ['unpaid-total'],
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
  },
  transaction: {
    singular: 'transaction',
    plural: 'transactions',
    queryKeyPatterns: ['transaction', 'transactions'],
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
    usesInfiniteQueries: true,
  },
  /**
   * Bilateral AR/AP balance between two parties (relations-v2).
   * Account mutations are driven by invoice captures and account
   * transactions — both write paths bust this key so the
   * counterparty balance card refreshes immediately.
   */
  account: {
    singular: 'account',
    plural: 'accounts',
    queryKeyPatterns: [
      'account',
      'accounts',
      // A new captureOrderLeg writes both an Invoice and an
      // AccountTransaction; bust them together so list views
      // refresh in one round trip.
      'invoice',
      'invoices',
      'account-transaction',
      'account-transactions',
    ],
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
    usesInfiniteQueries: true,
  },
  /**
   * `AccountTransaction` rows — the bilateral equivalent of
   * `Transaction`. Mutations also touch the parent Account
   * (balance / lastActivityAt), so we invalidate both here.
   */
  accountTransaction: {
    singular: 'account-transaction',
    plural: 'account-transactions',
    queryKeyPatterns: [
      'account-transaction',
      'account-transactions',
      'account',
      'accounts',
    ],
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
    usesInfiniteQueries: true,
  },
  clientConfig: {
    singular: 'client-config',
    plural: 'client-configs',
    queryKeyPatterns: ['client-config'],
    staleTime: 5 * 60 * 1000,
  },
  app: {
    singular: 'app',
    plural: 'apps',
    queryKeyPatterns: ['app', 'apps'],
    staleTime: 5 * 60 * 1000,
  },
  cashback: {
    singular: 'cashback',
    plural: 'cashbacks',
    queryKeyPatterns: ['cashback', 'current-user-cashback'],
    staleTime: Infinity,
  },
  /**
   * Section-keyed `/org/profile` reads + writes (relations-v2). A
   * single mutation here can touch `Org.name`, `Org.logo`,
   * `Org.legal`, `Bank`, or `Org.settings` — so invalidation has to
   * bust everything that reads any of those: the new `orgProfile`
   * key itself, and any `org` / `orgs` reads (the unified collection
   * backing each tab). Connections + clients also denormalize
   * `org.name` / `org.logo`, so include them so the network/CRM
   * cards refresh.
   *
   * `current-user` is included because the auth payload embeds
   * `business.name` / `carrier.name` / `business.logo` — these
   * drive the dashboard's top-right profile chip and side nav.
   * Without busting it, renaming the org in the profile tab would
   * leave the header reading a stale snapshot (the `currentUser`
   * cache is persisted to localStorage with a 10-min staleTime).
   */
  orgProfile: {
    singular: 'org-profile',
    plural: 'org-profiles',
    queryKeyPatterns: [
      'org-profile',
      'org-profiles',
      'orgProfile',
      'org',
      'orgs',
      'business',
      'businesses',
      'connection',
      'connections',
      'client',
      'clients',
      'current-user',
    ],
    staleTime: 30 * 1000,
  },
  currentUser: {
    singular: 'current-user',
    plural: 'current-users',
    queryKeyPatterns: ['current-user'],
    staleTime: 10 * 60 * 1000, // 10 min — user identity rarely changes mid-session
    gcTime: PERSIST_MAX_AGE, // 24 h — persisted to localStorage, needs long gcTime
    refetchOnWindowFocus: true,
    persist: true,
    // A 401 here usually means the session is dead — don't fan out
    // 3 extra `/auth/me` calls via React Query's default retry.
    retry: false,
  },
  route: {
    singular: 'route',
    plural: 'routes',
    // Pricing routes affect resolver output, which is consumed by orders,
    // offers, and the price-calc APIs. Bust those caches on any Route
    // mutation to avoid stale prices showing up in create-order screens.
    queryKeyPatterns: ['route', 'routes', 'agreement', 'agreements', 'price', 'order', 'offer'],
    usesInfiniteQueries: true,
  },
  agreement: {
    singular: 'agreement',
    plural: 'agreements',
    // Agreement status changes (e.g. terminate) cascade-archive linked
    // Routes server-side. Invalidate routes here so the UI reflects that.
    queryKeyPatterns: ['agreement', 'agreements', 'route', 'routes'],
    usesInfiniteQueries: true,
  },
  region: {
    singular: 'region',
    plural: 'regions',
    // Regions / cities are reference data — rarely mutated and safe to cache
    // for the session. `useTranslateRegions` and `useRegionSearchApi` both
    // mount under this entity so a Super-Admin region edit invalidates both
    // surfaces atomically.
    queryKeyPatterns: [
      'region',
      'regions',
      'cities',
      'region-translate',
      'region-search',
    ],
    // 24h stale + infinite GC mirrors the backend's `Cache-Control: max-age=86400`
    // on `/region/translate`. Region docs change ~once per quarter (importer run).
    staleTime: 24 * 60 * 60 * 1000,
    gcTime: Number.POSITIVE_INFINITY,
  },
  // Relations-v2 entities (Phase 5). See `docs/relations-v2/`.
  connection: {
    singular: 'connection',
    plural: 'connections',
    // Connection counters bump on Order/Offer/Agreement writes; bust the
    // related caches so the "My Network" surface stays fresh.
    queryKeyPatterns: [
      'connection',
      'connections',
      'order',
      'orders',
      'offer',
      'offers',
      'agreement',
      'agreements',
    ],
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
    usesInfiniteQueries: true,
  },
  profile: {
    singular: 'profile',
    plural: 'profiles',
    // Brand-directory reads are admin-curated; rarely mutated outside the
    // admin panel. Long stale time, but bust clients when a Profile updates
    // so linked Client cards refresh.
    queryKeyPatterns: ['profile', 'profiles', 'client', 'clients'],
    staleTime: 5 * 60 * 1000,
    usesInfiniteQueries: true,
  },
  org: {
    singular: 'org',
    plural: 'orgs',
    // Unified Business+Carrier read surface. Phase 6 promotes Org to
    // primary; for now reads are best-effort and writes still go through
    // the legacy business / carrier endpoints.
    queryKeyPatterns: ['org', 'orgs', 'business', 'businesses', 'carrier', 'carriers'],
    staleTime: 60 * 1000,
    usesInfiniteQueries: true,
  },
  entityDirectory: {
    singular: 'entity-directory-hit',
    plural: 'entity-directory-hits',
    // Unified Profile + Org search powering the Client-create picker.
    // Bust when either upstream cache changes so freshly-created
    // Profiles / Orgs appear without a hard refresh.
    queryKeyPatterns: ['entity-directory', 'profile', 'profiles', 'org', 'orgs'],
    staleTime: 30 * 1000,
  },
  // Sales domain (Hero subscriptions + billing).
  subscription: {
    singular: 'subscription',
    plural: 'subscriptions',
    // Mutations on a Subscription (change plan / seats / addons, cancel,
    // renew) create Billings and BTxs; bust those caches together and
    // also the resolved Features payload so the UI updates immediately.
    queryKeyPatterns: [
      'subscription',
      'subscriptions',
      'billing',
      'billings',
      'billing-transaction',
      'billing-transactions',
      'features',
    ],
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
  },
  billing: {
    singular: 'billing',
    plural: 'billings',
    // Payments / refunds on a Billing change the parent Subscription's
    // status. Invalidate subscription + features so "you're past due"
    // splash clears after a successful payment.
    queryKeyPatterns: [
      'billing',
      'billings',
      'subscription',
      'subscriptions',
      'billing-transaction',
      'billing-transactions',
      'features',
    ],
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
    usesInfiniteQueries: true,
  },
  billingTransaction: {
    singular: 'billing-transaction',
    plural: 'billing-transactions',
    queryKeyPatterns: ['billing-transaction', 'billing-transactions', 'billing', 'billings'],
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
    usesInfiniteQueries: true,
  },
  features: {
    singular: 'features',
    plural: 'features',
    queryKeyPatterns: ['features', 'subscription', 'subscriptions'],
    staleTime: 60 * 1000,
    refetchOnWindowFocus: true,
  },
  // Embedded-financing v0 pilot. The carrier "applications" surface
  // and the partner-view are both keyed off the same upstream
  // collection, so we invalidate both on every mutation (apply,
  // accept-offer, partner submit-offer, outcome).
  financingApplication: {
    singular: 'financing-application',
    plural: 'financing-applications',
    queryKeyPatterns: [
      'financing-application',
      'financing-applications',
      'financing-partner-view',
      // Apply / accept-offer can write to Org.financingProfile.
      'org',
      'orgs',
    ],
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
    usesInfiniteQueries: true,
  },
  /**
   * Admin-managed lender directory (financing partners).
   *
   * Lives in its own entity so admin CRUD writes can bust the
   * public `getActive` cache + the legacy `financing-partner` /
   * `financingPartner` keys used by the carrier dashboard.
   */
  lender: {
    singular: 'lender',
    plural: 'lenders',
    queryKeyPatterns: [
      'lender',
      'lenders',
      'financing-partner',
      'financing-partners',
      'financingPartner',
    ],
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
    usesInfiniteQueries: true,
  },
  /**
   * Legacy alias used by `useFinancingPartnersListApi`. The data is
   * now sourced from the active lender list, but we keep a separate
   * entity so callers binding to `financing-partner` keep working
   * during the cutover.
   */
  financingPartner: {
    singular: 'financing-partner',
    plural: 'financing-partners',
    queryKeyPatterns: ['financing-partner', 'financing-partners', 'lender', 'lenders'],
    staleTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
  },
  /**
   * Lender-view of a single application (one row per offer-from-
   * lender). Backs the unauthenticated
   * `/financing-application/:appId?i=<lenderId>` page (legacy alias:
   * `/financing/partner/:appId?p=<lenderId>`). Express-interest +
   * submit-offer mutations invalidate this key AND the upstream
   * application row so the carrier dashboard sees the partner's
   * action on its next refetch.
   *
   * `persist: true` is what lets the Tarmeez-style lender branding
   * (logo, name, brand colour) survive a hard refresh — the
   * dehydrated response rehydrates from `localStorage` before the
   * single-shot fetch resolves, so the hero banner never flickers
   * back to the neutral fallback.
   */
  financingPartnerView: {
    singular: 'financing-partner-view',
    plural: 'financing-partner-views',
    queryKeyPatterns: [
      'financing-partner-view',
      'financing-application',
      'financing-applications',
    ],
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
    persist: true,
  },
  /**
   * Hero AI agent — chat conversations + transcripts. The streaming
   * `sendMessage` does not flow through TanStack Query (it's an
   * async iterator); the transcript / activities / list reads
   * registered later (Phase 2) will use this entity for cache
   * invalidation so a confirm-action mutation refreshes the
   * conversation list and unread count.
   */
  /**
   * Chat surface, but the mutation under it (`confirmMessage`) is a
   * write to whatever the card staged — an order edit, a driver
   * assignment, a new client, a saved place. Confirming from chat and
   * then opening the order drawer has to show the new state, so this
   * entity busts every domain a confirmation can commit to rather than
   * just the transcript it lives in.
   */
  agent: {
    singular: 'agent-conversation',
    plural: 'agent-conversations',
    queryKeyPatterns: [
      'agent',
      'agent-conversation',
      'agent-conversations',
      'agent-message',
      'agent-messages',
      'agent-activity',
      'agent-activities',
      'order',
      'orders',
      'loads',
      'active-orders',
      'published-orders',
      'jobs',
      'analytics-overview',
      'driver',
      'drivers',
      'vehicle',
      'vehicles',
      'client',
      'clients',
      'contact',
      'contacts',
      'saved-location',
      'saved-locations',
      'savedLocationsList',
      'waybill',
    ],
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  },
} as const satisfies Record<string, EntityCacheConfig>;

export type Entity = keyof typeof ENTITY_CONFIGS;
